// @story HIR-HR33
// One board per round (server: services/roundViews.service.js): the rounds
// in process order with their counts, and the chosen round in full — each
// candidate's mark, every panellist's own mark and who typed it in, HR and
// exit-filter decisions, what is still outstanding, and anyone out with the
// reason. Nobody is dropped from a board. Downloads as CSV.
import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { Button, StatusPill } from "../../ds";
import { Section, Note, Loading } from "../../components/page";

const muted = { color: "var(--text-muted)", fontSize: "var(--fs-caption)" };
const cell = { padding: "6px 8px", borderBottom: "1px solid var(--border)", fontSize: "var(--fs-body-sm)", verticalAlign: "top", textAlign: "left" };
const head = { ...cell, fontWeight: 600, color: "var(--text-1)" };

export default function RoundBoards({ jobId }) {
  const [list, setList] = useState(null);
  const [pick, setPick] = useState("");
  const [board, setBoard] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/round-views/vacancies/${jobId}/boards`)
      .then((r) => { const d = r.data.data; setList(d); setPick((p) => p || d.boards.find((b) => b.reached)?.stageId || d.boards[0]?.stageId || ""); })
      .catch((e) => setErr(e?.response?.data?.message || "The boards could not be read just now."));
  }, [jobId]);

  useEffect(() => {
    if (!pick) return;
    setBoard(null);
    api.get(`/round-views/vacancies/${jobId}/boards/${encodeURIComponent(pick)}`)
      .then((r) => setBoard(r.data.data))
      .catch((e) => setErr(e?.response?.data?.message || "That board could not be read just now."));
  }, [jobId, pick]);

  const download = () => {
    api.get(`/round-views/vacancies/${jobId}/boards/${encodeURIComponent(pick)}`, { params: { format: "csv" }, responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${board?.label || "round"}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => setErr("The CSV could not be downloaded just now."));
  };

  if (err) return <Note tone="warning">{err}</Note>;
  if (!list) return <Loading label="Reading the boards" />;
  return (
    <Section title="Boards" subtitle="One board per round, in process order. Candidates who are out stay on the board with the reason.">
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }} role="tablist" aria-label="Rounds">
        {list.boards.map((b) => (
          <Button key={b.stageId} size="sm" variant={b.stageId === pick ? "primary" : "secondary"} onClick={() => setPick(b.stageId)}>
            {b.label} · {b.reached}
          </Button>
        ))}
      </div>
      {!board ? <Loading label="Reading the board" /> : (
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <span style={muted}>{board.reached} reached · {board.marked} marked · {board.outstanding} with something outstanding · {board.out} out</span>
            {board.referenceOnly && <StatusPill tone="neutral">Reference only</StatusPill>}
            {board.noMarks && <StatusPill tone="neutral">No marks</StatusPill>}
            <Button size="sm" variant="ghost" onClick={download} disabled={!board.reached}>Download CSV</Button>
          </div>
          {!board.reached ? <Note tone="info">Nobody has reached this round yet.</Note> : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%" }} data-testid="round-board">
                <thead>
                  <tr>
                    <th style={head}>Candidate</th><th style={head}>Mark</th><th style={head}>Panellists</th>
                    <th style={head}>Decisions</th><th style={head}>Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {board.candidates.map((c) => (
                    <tr key={c.candidateId} style={c.out ? { opacity: 0.75 } : undefined}>
                      <td style={cell}>
                        {c.name}<div style={muted}>Ref. {c.reference}</div>
                        {c.out && <div><StatusPill tone="danger">Out</StatusPill> <span style={muted}>{c.outReason}</span></div>}
                      </td>
                      <td style={cell} className="num">{c.noMarks ? (c.recorded || "—") : (c.mark ?? "—")}</td>
                      <td style={cell}>
                        {c.panellists.length ? c.panellists.map((p, i) => (
                          <div key={i}>{p.name}: {p.score ?? <span style={muted}>not yet</span>}{p.enteredBy && <span style={muted}> (typed by {p.enteredBy})</span>}</div>
                        )) : <span style={muted}>—</span>}
                      </td>
                      <td style={cell}>
                        {c.hrRuling && <div>HR {c.hrRuling.decision}: {c.hrRuling.reason}</div>}
                        {c.exitFilter && <div>Exit filter {c.exitFilter.decision}: {c.exitFilter.reason}</div>}
                        {!c.hrRuling && !c.exitFilter && <span style={muted}>—</span>}
                      </td>
                      <td style={cell}>{c.outstanding.length ? c.outstanding.join("; ") : <span style={muted}>Nothing</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}
